import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import GemmaWorker from "./gemma.worker.ts?worker";
import type { WorkerRequest, WorkerResponse } from "./workerProtocol";
import type { FileLoadProgress, GemmaStatus, GenerateRequest } from "./types";
import { GemmaContext, type GemmaContextValue } from "./gemmaContext";

type PendingLoad = {
  resolve: () => void;
  reject: (err: Error) => void;
};

type PendingGenerate = {
  resolve: (text: string) => void;
  reject: (err: Error) => void;
  onToken?: (chunk: string) => void;
};

const supportsWebGPU = (): boolean =>
  typeof navigator !== "undefined" && "gpu" in navigator;

export const GemmaProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<GemmaStatus>({ kind: "unloaded" });
  const workerRef = useRef<Worker | null>(null);
  const nextIdRef = useRef(1);
  const loadsRef = useRef(new Map<number, PendingLoad>());
  const gensRef = useRef(new Map<number, PendingGenerate>());
  const isLoadedRef = useRef(false);
  const filesRef = useRef(new Map<string, FileLoadProgress>());
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  const cleanupWorker = useCallback((reason: Error | null) => {
    workerRef.current?.terminate();
    workerRef.current = null;
    isLoadedRef.current = false;
    if (reason) {
      for (const p of loadsRef.current.values()) p.reject(reason);
      for (const p of gensRef.current.values()) p.reject(reason);
    }
    loadsRef.current.clear();
    gensRef.current.clear();
  }, []);

  const ensureWorker = useCallback((): Worker => {
    if (workerRef.current) return workerRef.current;
    const w = new GemmaWorker();
    w.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
      const msg = event.data;
      switch (msg.type) {
        case "loadProgress": {
          const info = msg.info;
          if (!info.file) break;
          const prev = filesRef.current.get(info.file);
          const isDone = info.status === "done";
          const progress = isDone
            ? 100
            : Math.min(100, Math.max(0, info.progress ?? prev?.progress ?? 0));
          filesRef.current.set(info.file, {
            file: info.file,
            progress,
            state: isDone ? "done" : "downloading",
          });
          setStatus({
            kind: "loading",
            files: Array.from(filesRef.current.values()),
          });
          break;
        }
        case "loaded": {
          isLoadedRef.current = true;
          filesRef.current.clear();
          setStatus({ kind: "ready" });
          const p = loadsRef.current.get(msg.id);
          if (p) {
            p.resolve();
            loadsRef.current.delete(msg.id);
          }
          break;
        }
        case "loadError": {
          const files = Array.from(filesRef.current.values());
          setStatus({
            kind: "error",
            message: msg.message,
            files: files.length > 0 ? files : undefined,
          });
          const p = loadsRef.current.get(msg.id);
          if (p) {
            p.reject(new Error(msg.message));
            loadsRef.current.delete(msg.id);
          }
          break;
        }
        case "token": {
          const p = gensRef.current.get(msg.id);
          p?.onToken?.(msg.chunk);
          break;
        }
        case "generateDone": {
          setStatus({ kind: "ready" });
          const p = gensRef.current.get(msg.id);
          if (p) {
            p.resolve(msg.text);
            gensRef.current.delete(msg.id);
          }
          break;
        }
        case "generateError": {
          setStatus({ kind: "ready" });
          const p = gensRef.current.get(msg.id);
          if (p) {
            p.reject(new Error(msg.message));
            gensRef.current.delete(msg.id);
          }
          break;
        }
      }
    });
    w.addEventListener("error", (event) => {
      cleanupWorker(new Error(event.message || "Worker crashed"));
      setStatus({ kind: "error", message: event.message || "Worker crashed" });
    });
    workerRef.current = w;
    return w;
  }, [cleanupWorker]);

  const load = useCallback((): Promise<void> => {
    if (!supportsWebGPU()) {
      const message = "WebGPU is not available in this browser.";
      setStatus({ kind: "error", message });
      return Promise.reject(new Error(message));
    }
    if (isLoadedRef.current) return Promise.resolve();
    const worker = ensureWorker();
    const id = nextIdRef.current++;
    filesRef.current.clear();
    setStatus({ kind: "loading", files: [] });
    return new Promise<void>((resolve, reject) => {
      loadsRef.current.set(id, { resolve, reject });
      const req: WorkerRequest = { type: "load", id };
      worker.postMessage(req);
    });
  }, [ensureWorker]);

  const generate = useCallback(
    (req: GenerateRequest): Promise<string> => {
      const run = async (): Promise<string> => {
        await load();
        const worker = ensureWorker();
        const id = nextIdRef.current++;
        setStatus({ kind: "busy" });
        return new Promise<string>((resolve, reject) => {
          gensRef.current.set(id, {
            resolve,
            reject,
            onToken: req.onToken,
          });
          const message: WorkerRequest = {
            type: "generate",
            id,
            prompt: req.prompt,
            image: req.image,
            maxNewTokens: req.maxNewTokens ?? 512,
          };
          worker.postMessage(message);
        });
      };
      const next = queueRef.current.then(run, run);
      queueRef.current = next.catch(() => undefined);
      return next;
    },
    [ensureWorker, load],
  );

  const unload = useCallback(() => {
    cleanupWorker(new Error("Model unloaded"));
    filesRef.current.clear();
    setStatus({ kind: "unloaded" });
  }, [cleanupWorker]);

  const value = useMemo<GemmaContextValue>(
    () => ({ status, generate, load, unload }),
    [status, generate, load, unload],
  );

  return (
    <GemmaContext.Provider value={value}>{children}</GemmaContext.Provider>
  );
};
